import { PermissionEnum } from '@api/enum';
import { IDocumentFolderModel, IDocumentFolderWithAccess, IExternalLinkModel, IUser } from '@api/models';
import OverflowTooltip from '@components/OverflowTooltip/OverflowTooltip';
import useAuthPermission from '@hooks/use-auth-permission';
import { Avatar, AvatarGroup } from '@library/avatar';
import { TRootState } from '@store/index';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { TCell, TRow } from '../DocumentList/DocumentList';
import { EllipsisVerticalIcon, FolderIcon, PlusIcon } from '@icon/index';
import TzDateFormatter from 'modules/shared/helpers/TzDateFormatter';
import EventBlocker from '@components/EventBlocker/EventBlocker';
import { Dropdown, DropdownItem, DropdownList, DropdownTrigger } from '@library/dropdown';
import DropdownPortal from '@library/dropdown/components/DropdownPortal';
import clsx from 'clsx';
import { Card, Table } from '@hyperflake/react-ui-library';
import FolderBreadcrumbs from 'modules/box-strorage/feature/folder-breadcrumb/FolderBreadcrumb';
import { Button } from '@components/buttons';
import { convertFileSize } from 'modules/shared/helpers/shared.utils';

interface ExternalLinkListProps {
    breadcrumb: { name: string; folderId: string }[];
    folders: IDocumentFolderWithAccess[];
    externalLinks: IExternalLinkModel[];
    onFolderClick: (folderId: string) => void;
    onFolderEdit?: (folder: IDocumentFolderModel) => void;
    onFolderDelete?: (folder: IDocumentFolderModel) => void;
    onUserAccessClick: (externalLink: IExternalLinkModel) => void;
    onMoveExternalLinkClick?: (externalLink: IExternalLinkModel) => void;
    onMoveFolderClick?: (folder: IDocumentFolderModel) => void;
    onExternalLinkClick: (externalLink: IExternalLinkModel) => void;
    onDeleteClick: (externalLink: IExternalLinkModel) => void;
    onAddExternalLinkClick: () => void;
    onAddFolderClick: () => void;
    onRenameExternalLinkClick: (externalLink: IExternalLinkModel) => void;
}

type TableItem =
    | { _id: string; type: 'folder'; data: IDocumentFolderWithAccess }
    | { _id: string; type: 'link'; data: IExternalLinkModel };

const ExternalLinkList = (props: ExternalLinkListProps) => {
    const {
        breadcrumb,
        folders,
        externalLinks,
        onFolderClick,
        onFolderEdit,
        onFolderDelete,
        onUserAccessClick,
        onExternalLinkClick,
        onDeleteClick,
        onAddExternalLinkClick,
        onAddFolderClick,
        onMoveFolderClick,
        onMoveExternalLinkClick,
        onRenameExternalLinkClick,
    } = props;

    const { hasPermission } = useAuthPermission();

    const [searchParams, setSearchParams] = useSearchParams();

    // const { user: authUser } = useAuthSelector();
    const { info: authUser } = useSelector((state: TRootState) => state.profile);

    const selectedExternalIdRef = useRef<string | null>(null);
    const selectedTab = searchParams.get('tab');
    const selectedExternalId = searchParams.get('id');

    const [sortBy, setSortBy] = useState<string>('');
    const [sortOrder, setSortOrder] = useState<number>(1);

    // Combine folders and links into a single sortable array
    // const combinedItems = useMemo<TableItem[]>(() => {
    //     const folderItems: TableItem[] = folders.map((folder) => ({ type: 'folder' as const, data: folder }));
    //     const linkItems: TableItem[] = externalLinks.map((link) => ({ type: 'link' as const, data: link }));

    //     return [...folderItems, ...linkItems];
    // }, [folders, externalLinks]);

    const tableItems = useMemo(() => {
        const folderItems: TableItem[] = folders.map((folder) => ({
            _id: folder._id,
            type: 'folder' as const,
            data: folder,
        }));

        const externalLinkItems: TableItem[] = externalLinks.map((file) => ({
            _id: file._id,
            type: 'link' as const,
            data: file,
        }));

        return [...folderItems, ...externalLinkItems];
    }, [externalLinks, folders]);

    const sortedItems = useMemo(() => {
        if (!sortBy) {
            return tableItems;
        }

        return tableItems.sort((a, b) => {
            // Always put folders first
            if (a.type === 'folder' && b.type === 'link') return -1;
            if (a.type === 'link' && b.type === 'folder') return 1;

            // Sort within same type
            const aValue = (a.data as any)?.[sortBy];
            const bValue = (b.data as any)?.[sortBy];

            if (!aValue) return 0;
            if (!bValue) return 0;

            if (sortOrder === 1) {
                return aValue.localeCompare ? aValue.localeCompare(bValue) : 0;
            } else {
                return bValue.localeCompare ? bValue.localeCompare(aValue) : 0;
            }
        });
    }, [sortBy, sortOrder, tableItems]);

    const handleCopyExternalLink = (external_link: string) => {
        navigator.clipboard.writeText(external_link);
        toast.success('Copied Successfully.');
    };

    useEffect(() => {
        selectedExternalIdRef.current = selectedExternalId;
    }, [selectedExternalId, selectedTab]);

    useEffect(() => {
        if (selectedExternalIdRef.current) {
            const element = document.getElementById(`external-link-row-${selectedExternalIdRef.current}`);

            if (element) {
                setTimeout(() => {
                    element.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                    });
                }, 100);
            }

            const timer = setTimeout(() => {
                const newParams = new URLSearchParams(searchParams);
                newParams.delete('id');

                setSearchParams(newParams, { replace: true });
            }, 8000);
            return () => clearTimeout(timer);
        }
    }, [sortedItems, searchParams, setSearchParams]);

    const handleSortChange = (sortBy: string, sortOrder: number) => {
        setSortBy(sortBy);
        setSortOrder(sortOrder);
    };

    const renderUploadedBy = (user: IUser) => {
        if (user._id === authUser._id)
            return (
                <div key={user._id} className="flex items-center gap-3">
                    <div>
                        <Avatar src={user.dpUrl} alt={user.name} />
                    </div>

                    <div>You</div>
                </div>
            );

        return (
            <div className="flex items-center gap-3">
                <div>
                    <Avatar src={user.dpUrl} alt={user.name} />
                </div>

                <div className="overflow-hidden">
                    <OverflowTooltip>{user.name}</OverflowTooltip>
                    <div className="text-xs font-normal text-grayscale-500">
                        {user.designation ? (
                            <OverflowTooltip>{user.designation}</OverflowTooltip>
                        ) : (
                            <i>No designation</i>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderFolderRow = (folder: IDocumentFolderWithAccess) => {
        const createdBy = folder.createdBy as IUser;
        const canEdit = hasPermission(PermissionEnum.DOCUMENT_UPDATE) || createdBy._id === authUser._id;
        const canDelete = hasPermission(PermissionEnum.DOCUMENT_DELETE) || createdBy._id === authUser._id;
        // const counts = getFolderCounts ? getFolderCounts(folder._id) : { filesCount: 0, linksCount: 0 };

        return (
            <TRow
                key={`folder-${folder._id}`}
                className="bg-card-color hover:bg-grayscale-50 cursor-pointer"
                onClick={() => onFolderClick(folder._id)}
            >
                <TCell maxWidth="14vw">
                    <div className="flex items-center gap-3">
                        <div>
                            {/* <FolderIcon width="28" height="28" className="text-primary-500" /> */}
                            {/* <img className="w-7" src="/icons/folder.svg" alt="" />
                             */}
                            <FolderIcon />
                        </div>
                        <div>
                            <OverflowTooltip>{folder.name}</OverflowTooltip>
                        </div>
                    </div>
                </TCell>
                <TCell>
                    <TzDateFormatter date={folder.createdAt} format={'DD MMM, YYYY'} />
                </TCell>
                {/* <TCell>{convertFileSize(+folder.folderSize)}</TCell> */}
                <TCell maxWidth="15vw">{renderUploadedBy(createdBy)}</TCell>

                <TCell>
                    <div>
                        <AvatarGroup
                            avatars={(folder.fileUsers as IUser[]).map((row) => ({
                                src: row.dpUrl,
                                alt: row.name,
                                backgroundColor: row?.preset?.color,
                            }))}
                        />
                    </div>
                </TCell>

                <TCell>
                    <EventBlocker stopPropagation>
                        <Dropdown>
                            <DropdownTrigger className="btn-action">
                                <EllipsisVerticalIcon className="text-grayscale-500" />
                            </DropdownTrigger>

                            <DropdownPortal>
                                <DropdownList>
                                    <DropdownItem disabled={!canEdit} onClick={() => onFolderEdit(folder)}>
                                        Rename
                                    </DropdownItem>
                                    <DropdownItem onClick={() => onMoveFolderClick(folder)}>Move</DropdownItem>

                                    <DropdownItem disabled={!canDelete} onClick={() => onFolderDelete(folder)}>
                                        <span className="text-danger-500">Delete</span>
                                    </DropdownItem>
                                </DropdownList>
                            </DropdownPortal>
                        </Dropdown>
                    </EventBlocker>
                </TCell>
            </TRow>
        );
    };

    const renderLinkRow = (link: IExternalLinkModel) => {
        const createdBy = link.uploadedBy as IUser;
        return (
            <TRow
                key={`link-${link._id}`}
                id={`external-link-row-${link._id}`}
                className={clsx('bg-card-color', selectedExternalId === link._id && 'animate-bg-fade')}
            >
                <TCell maxWidth="14vw">
                    <OverflowTooltip>{link.name}</OverflowTooltip>
                </TCell>

                <TCell>
                    <TzDateFormatter date={link.createdAt} format={'DD MMM, YYYY'} />
                </TCell>

                <TCell maxWidth="15vw">{renderUploadedBy(createdBy)}</TCell>
                {/* <TCell>{convertFileSize(+file.fileSize)}</TCell> */}
                <TCell>
                    <div className="cursor-pointer" onClick={() => onUserAccessClick(link)}>
                        <AvatarGroup
                            avatars={(link.users as IUser[]).map((row) => ({
                                src: row.dpUrl,
                                alt: row.name,
                                backgroundColor: row.preset?.color,
                            }))}
                        />
                    </div>
                </TCell>

                <TCell>
                    <Dropdown>
                        <DropdownTrigger className="btn-action">
                            <EllipsisVerticalIcon className="text-grayscale-500" />
                        </DropdownTrigger>

                        <DropdownPortal>
                            <DropdownList>
                                <DropdownItem onClick={() => onExternalLinkClick(link)}>View</DropdownItem>
                                <DropdownItem onClick={() => handleCopyExternalLink(link.path)}>Copy</DropdownItem>
                                <DropdownItem onClick={() => onMoveExternalLinkClick(link)}>Move</DropdownItem>
                                <DropdownItem onClick={() => onRenameExternalLinkClick(link)}>Rename</DropdownItem>
                                <DropdownItem
                                    disabled={
                                        !(
                                            hasPermission(PermissionEnum.DOCUMENT_DELETE) ||
                                            (link.uploadedBy as IUser)._id === authUser._id
                                        )
                                    }
                                    onClick={() => onDeleteClick(link)}
                                >
                                    <span className="text-danger-500">Delete</span>
                                </DropdownItem>
                            </DropdownList>
                        </DropdownPortal>
                    </Dropdown>
                </TCell>
            </TRow>
        );
    };

    return (
        <>
            <Card variant="wrapper" className="overflow-hidden">
                <div className="p-4 pb-0 overflow-x-auto">
                    <div className="flex items-end sm:items-center justify-between gap-4 ">
                        <FolderBreadcrumbs
                            items={[{ name: 'All External Links', folderId: null }, ...breadcrumb]}
                            onItemClick={onFolderClick}
                        />

                        <div className="flex flex-col sm:flex-row items-end sm:items-center gap-4 ml-auto">
                            <Button color="secondary" size="xs" onClick={onAddFolderClick}>
                                <PlusIcon width="10" height="10" /> Add Folder
                            </Button>

                            <Button size="xs" onClick={onAddExternalLinkClick}>
                                <PlusIcon width="10" height="10" /> Add External Link
                            </Button>
                        </div>
                    </div>

                    {/* <Table
                        columns={[
                            { header: 'Name', id: 'name', sortable: true },
                            { header: 'Date', id: 'createdAt', sortable: true },
                            { header: 'Created By', sortable: false },
                            { header: 'Access', sortable: false },
                            { header: 'Action', sortable: false },
                        ]}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSortChange={handleSortChange}
                        data={sortedItems}
                        renderRow={(item) => {
                            if (item.type === 'folder') {
                                return renderFolderRow(item.data);
                            } else {
                                return renderLinkRow(item.data);
                            }
                        }}
                    /> */}

                    <Table
                        className="mt-2"
                        borderStyle="divider"
                        hover
                        // isLoading={isLoading} // if you have it
                        data={sortedItems}
                        columns={[
                            { header: 'Name' },
                            { header: 'Date' },
                            // { header: 'File Size' },
                            { header: 'Created By' },
                            { header: 'Access' },
                            { header: 'Action' },
                        ]}
                        renderRow={(item) => {
                            if (item.type === 'folder') {
                                return renderFolderRow(item.data);
                            } else {
                                return renderLinkRow(item.data);
                            }
                        }}
                    />
                </div>
            </Card>
        </>
    );
};

export default ExternalLinkList;
